import { portalPath } from '../utils/portalPath';
import React, { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { AUTH_DISABLED, useAuth } from '../contexts/AuthContext';
import { useImpersonation } from '../contexts/ImpersonationContext';

interface PrivateRouteProps {
  children: ReactNode;
}

const PrivateRoute: React.FC<PrivateRouteProps> = ({ children }) => {
  const {
    session,
    currentUser,
    loading,
    needsOnboarding,
    signOut,
  } = useAuth();
  const { effectiveRole } = useImpersonation();
  const location = useLocation();

  // Local-dev bypass has no Supabase session; the backend vouches for the user.
  if (!loading && !session && !AUTH_DISABLED) {
    return (
      <Navigate
        to="/login"
        replace
        state={{ from: location.pathname + location.search }}
      />
    );
  }

  // A token is sufficient for API authorization. Mount deep-linked pages while
  // bootstrap resolves; profile-based redirects still run once it arrives.
  if (loading && session) {
    const role = effectiveRole ?? currentUser?.role ?? session.user?.app_metadata?.role ?? session.user?.user_metadata?.role;
    if (role === 'client' || role === 'facility_manager') {
      if (!location.pathname.startsWith('/portal') && location.pathname !== '/onboarding') {
        return <Navigate to={portalPath(location.pathname + location.search)} replace />;
      }
    }
    if (location.pathname === '/' && role === 'h2okay_admin') return <Navigate to="/admin" replace />;
    if (location.pathname !== '/' || role) return <>{children}</>;
  }

  if (loading) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
          color: 'var(--text-secondary)',
        }}
      >
        Loading…
      </div>
    );
  }

  if (!currentUser) {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '1rem',
          height: '100vh',
          color: 'var(--text-secondary)',
        }}
      >
        <p>Could not load your H2Okay profile.</p>
        <button type="button" className="btn btn-primary" onClick={() => signOut()}>
          Sign out
        </button>
      </div>
    );
  }

  // First-login gate: bounce to /onboarding until the user finishes it.
  if (needsOnboarding && location.pathname !== '/onboarding') {
    return <Navigate to="/onboarding" replace />;
  }

  // Role-based landing from the root path. Redirect each role to its
  // own shell; non-root deep links stay where they are so existing
  // bookmarks keep working.
  //
  // When an admin is acting-as a consultant, ``effectiveRole`` is
  // 'consultant' — fall through to the default (render children) so
  // they land on the consultant dashboard at /.
  const roleForLanding = effectiveRole ?? currentUser.role;
  const portalRole = roleForLanding === 'client' || roleForLanding === 'facility_manager';
  if (!needsOnboarding && portalRole && !location.pathname.startsWith('/portal') && location.pathname !== '/onboarding') {
    return <Navigate to={portalPath(location.pathname + location.search)} replace />;
  }
  if (location.pathname.startsWith('/admin') && roleForLanding !== 'h2okay_admin') {
    return <Navigate to={portalRole ? '/portal' : '/'} replace />;
  }
  if (!needsOnboarding && location.pathname === '/') {
    if (roleForLanding === 'h2okay_admin') {
      return <Navigate to="/admin" replace />;
    }
    if (
      roleForLanding === 'client' ||
      roleForLanding === 'facility_manager'
    ) {
      return <Navigate to="/portal" replace />;
    }
  }

  return <>{children}</>;
};

export default PrivateRoute;
